import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { getUsers, getCharities, getPledges, refreshData, clearDataCache } from '../services/dataService';
import { getCachedData } from '../services/cacheService';
import { deleteUser as apiDeleteUser } from '../services/userService';
import { deleteCharity as apiDeleteCharity } from '../services/charityService';
import { deletePledge as apiDeletePledge } from '../services/pledgeService';
import LoadingSpinner from './common/LoadingSpinner';
import ConfirmModal from './common/ConfirmModal';
import AddItemModal from './modals/AddItemModal';
import CharityForm from './forms/CharityForm';
import PledgeForm from './forms/PledgeForm';

const HomeView = () => {
  const { user } = useContext(AuthContext);
  
  // Initialize activeTab from localStorage or default to 'users'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('homeViewActiveTab');
    return savedTab && ['users', 'charities', 'pledges'].includes(savedTab) ? savedTab : 'users';
  });
  const [users, setUsers] = useState([]);
  const [charities, setCharities] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // { type: 'user' | 'charity' | 'pledge', id: string }
  const [deleteMessage, setDeleteMessage] = useState('');

  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormType, setAddFormType] = useState(null); // 'charity' | 'pledge'
  const [successMessage, setSuccessMessage] = useState('');

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('homeViewActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    const loadData = async () => {
      console.log('HomeView loadData called for tab:', activeTab);
      setLoading(true);
      setError(null);
      try {
        if (activeTab === 'users') {
          const usersData = await getUsers();
          console.log('Users Data Response:', usersData);
          console.log('Users Data Response type:', typeof usersData);
          console.log('Is Array?', Array.isArray(usersData));
          // Extract the users array from the response object
          setUsers(usersData.users || []);
        } else if (activeTab === 'charities') {
          const charitiesData = await getCharities();
          console.log('Charities Data Response:', charitiesData);
          console.log('Charities Data Response type:', typeof charitiesData);
          console.log('Is Array?', Array.isArray(charitiesData));
          // Extract the charities array from the response object
          setCharities(charitiesData.charities || []);
        } else if (activeTab === 'pledges') {
          // Load users and charities data to cache for name lookups
          await Promise.all([getUsers(), getCharities()]);
          
          const pledgesData = await getPledges();
          console.log('Pledges Data Response:', pledgesData);
          console.log('Pledges Data Response type:', typeof pledgesData);
          console.log('Is Array?', Array.isArray(pledgesData));
          
          // Handle direct array response (API returns array directly, not wrapped in object)
          if (Array.isArray(pledgesData)) {
            setPledges(pledgesData);
          } else {
            // Fallback for wrapped response format
            setPledges(pledgesData.pledges || []);
          }
        }
      } catch (err) {
        console.error(`Error fetching ${activeTab}:`, err);
        setError(`Failed to load ${activeTab}. Please try again.`);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [activeTab]);

  const handleRefresh = async () => {
    console.log('Refreshing data for tab:', activeTab);
    setRefreshing(true);
    setError(null);
    try {
      const refreshedData = await refreshData(activeTab);
      console.log('Refreshed Data Response:', refreshedData);
      
      if (activeTab === 'users') {
        setUsers(refreshedData.users || []);
      } else if (activeTab === 'charities') {
        setCharities(refreshedData.charities || []);
      } else if (activeTab === 'pledges') {
        if (Array.isArray(refreshedData)) {
          setPledges(refreshedData);
        } else {
          setPledges(refreshedData.pledges || []);
        }
      }
    } catch (err) {
      console.error(`Error refreshing ${activeTab}:`, err);
      setError(`Failed to refresh ${activeTab}. Please try again.`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteClick = async (type, id, name) => {
    console.log(`Delete clicked for ${type} with ID: ${id}, Name: ${name}`);
    
    if (type === 'charity') {
      try {
        // Check if charity has pledges
        const pledges = await getPledges();
        const charityPledges = Array.isArray(pledges) ?
          pledges.filter(p => p.charity_id === id) :
          (pledges.pledges || []).filter(p => p.charity_id === id);
          
        if (charityPledges.length > 0) {
          setError(`Cannot delete charity "${name}" - it has ${charityPledges.length} active pledge(s). Please delete or reassign pledges first.`);
          return;
        }
      } catch (err) {
        console.error('Error checking pledges:', err);
        // Continue with deletion attempt if we can't check pledges
      }
    } else if (type === 'user') {
      // Check cached pledges for user
     const pledges = await getPledges();
        const userPledges = Array.isArray(pledges) ?
          pledges.filter(p => p.user_id === id) :
          (pledges.pledges || []).filter(p => p.user_id === id);
          
        if (userPledges.length > 0) {
          setError(`Cannot delete user "${name}" - they have ${userPledges.length} active pledge(s). Please delete them before retrying.`);
          return;
        }
      else console.debug((`The user "${name}" has no pledges, proceeding with deletion.`));
    }
    else console.debug((`Trying to delete unknown type of object: ${type}`));
    
    setItemToDelete({ type, id });
    setDeleteMessage(`Are you sure you want to delete ${type} "${name || id}"? This action cannot be undone.`);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setLoading(true);
    try {
      if (itemToDelete.type === 'user') {
        try {
          await apiDeleteUser(itemToDelete.id);
          // Clear cache and force refresh
          clearDataCache('users');
          const refreshedData = await refreshData('users');
          setUsers(refreshedData.users || []);
        } catch (err) {
          // Handle case where API succeeds (204) but response isn't JSON
          if (err.message.includes('Unexpected end of JSON input')) {
            console.log('User deleted successfully (204 No Content)');
            // Still force refresh after successful deletion
            clearDataCache('users');
            const refreshedData = await refreshData('users');
            setUsers(refreshedData.users || []);
            return;
          }
          throw err;
        }
      } else if (itemToDelete.type === 'charity') {
        try {
          await apiDeleteCharity(itemToDelete.id);
          // Clear cache and force refresh
          clearDataCache('charities');
          const refreshedData = await refreshData('charities');
          setCharities(refreshedData.charities || []);
        } catch (err) {
          // Handle case where API succeeds (204) but response isn't JSON
          if (err.message.includes('Unexpected end of JSON input')) {
            console.log('Charity deleted successfully (204 No Content)');
            // Still force refresh after successful deletion
            clearDataCache('charities');
            const refreshedData = await refreshData('charities');
            setCharities(refreshedData.charities || []);
            return;
          }
          throw err;
        }
      } else if (itemToDelete.type === 'pledge') {
        try {
          console.log('Deleting pledge with ID:', itemToDelete.id);
          await apiDeletePledge(itemToDelete.id);
          // Clear cache and force refresh
          console.log('Clearing pledges cache...');
          clearDataCache('pledges');
          console.log('Refreshing pledges data...');
          const refreshedData = await refreshData('pledges');
          console.log('Refreshed pledges data:', refreshedData);
          
          // Ensure we always set an array, handling both direct array and wrapped responses
          const pledgesArray = Array.isArray(refreshedData) ?
            [...refreshedData] :
            [...(refreshedData.pledges || [])];
            
          console.log('Setting pledges state with:', pledgesArray);
          setPledges(pledgesArray);
        } catch (err) {
          // Handle case where API succeeds (204) but response isn't JSON
          if (err.message.includes('Unexpected end of JSON input')) {
            console.log('Pledge deleted successfully (204 No Content)');
            // Still force refresh after successful deletion
            clearDataCache('pledges');
            const refreshedData = await refreshData('pledges');
            setPledges(refreshedData.pledges || []);
            return;
          }
          throw err;
        }
      }
      console.log(`${itemToDelete.type} with ID ${itemToDelete.id} deleted successfully.`);
    } catch (err) {
      console.error(`Error deleting ${itemToDelete.type}:`, err);
      if (err.message.includes('Cannot delete charity with active pledges')) {
        setError('Cannot delete charity - it has active pledges. Please delete pledges first.');
      } else {
        setError(`Failed to delete ${itemToDelete.type}.`);
      }
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
      setItemToDelete(null);
    }
  };

  const handleAddClick = () => {
    if (activeTab === 'charities') {
      setAddFormType('charity');
    } else if (activeTab === 'pledges') {
      setAddFormType('pledge');
    }
    setShowAddModal(true);
  };

  const handleAddSuccess = async (message) => {
    setSuccessMessage(message);
    setShowAddModal(false);
    setAddFormType(null);
    
    // Refresh the data for the current tab
    try {
      const refreshedData = await refreshData(activeTab);
      if (activeTab === 'charities') {
        setCharities(refreshedData.charities || []);
      } else if (activeTab === 'pledges') {
        if (Array.isArray(refreshedData)) {
          setPledges(refreshedData);
        } else {
          setPledges(refreshedData.pledges || []);
        }
      }
    } catch (err) {
      console.error(`Error refreshing ${activeTab} after creation:`, err);
    }
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleAddError = (message) => {
    setError(message);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddFormType(null);
  };

  const renderTable = (data, type, columns) => {
    if (loading) return <LoadingSpinner />;
    if (!data || data.length === 0) return <p className="text-gray-600">No {type} found.</p>;

    return (
      <div className="overflow-x-auto shadow-lg rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 bg-white">
          <thead className="bg-gray-50">
            <tr>
              {columns.map(col => (
                <th key={col.key} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {col.header}
                </th>
              ))}
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map(item => (
              <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                {columns.map(col => (
                  <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {col.render ? col.render(item[col.key], item) : (item[col.key] === undefined || item[col.key] === null ? 'N/A' : String(item[col.key]))}
                  </td>
                ))}
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <Link to={`/${type}/${item.id}`} className="text-indigo-600 hover:text-indigo-900 mr-3">View/Edit</Link>
                  <button
                    onClick={() => handleDeleteClick(
                      type === 'charities' ? 'charity' :
                      type === 'pledges' ? 'pledge' :
                      'user',
                      item.id,
                      item.name || item.email
                    )}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Helper functions to lookup names from cache
  const getUserNameById = (userId) => {
    const cachedUsers = getCachedData('users');
    if (cachedUsers && cachedUsers.data && cachedUsers.data.users) {
      const user = cachedUsers.data.users.find(u => u.id === userId);
      return user ? user.name : '';
    }
    return '';
  };

  const getCharityNameById = (charityId) => {
    const cachedCharities = getCachedData('charities');
    if (cachedCharities && cachedCharities.data && cachedCharities.data.charities) {
      const charity = cachedCharities.data.charities.find(c => c.id === charityId);
      return charity ? charity.name : '';
    }
    return '';
  };

  const userColumns = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'is_admin', header: 'Admin', render: (val) => val ? 'Yes' : 'No' },
    { key: 'created_at', header: 'Created At', render: (val) => new Date(val).toLocaleDateString() },
  ];

  const charityColumns = [
    { key: 'name', header: 'Name' },
    {
      key: 'stripe_charges_enabled',
      header: 'Stripe',
      render: (_val, charity) => {
        const pledgeable = Boolean(charity.is_stripe_verified && charity.stripe_charges_enabled);
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            pledgeable ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {pledgeable ? 'Verified' : 'Unverified'}
          </span>
        );
      }
    },
    { key: 'website', header: 'Website', render: (val) => val ? <a href={val} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{val}</a> : 'N/A' },
    { key: 'created_at', header: 'Created At', render: (val) => new Date(val).toLocaleDateString() },
  ];

  const pledgeColumns = [
    { key: 'title', header: 'Title' },
    {
      key: 'user_id',
      header: 'User Name',
      render: (userId) => {
        const userName = getUserNameById(userId);
        return userName || '';
      }
    },
    {
      key: 'charity_id',
      header: 'Charity Name',
      render: (charityId) => {
        const charityName = getCharityNameById(charityId);
        return charityName || '';
      }
    },
    { key: 'amount_cents', header: 'Amount (Cents)', render: (val) => (val / 100).toFixed(2) },
    { key: 'status', header: 'Status' },
    { key: 'start_date', header: 'Start Date', render: (val) => new Date(val).toLocaleDateString() },
    { key: 'end_date', header: 'End Date', render: (val) => new Date(val).toLocaleDateString() },
  ];


  // Extract first name from user's full name or use given_name
  const getFirstName = (fullName, givenName) => {
    if (givenName) return givenName;
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  const userFirstName = getFirstName(user?.name, user?.given_name);

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">
        Hello {userFirstName ? `${userFirstName}` : 'Admin'} - Dashboard
      </h1>
      
      <div className="mb-6 border-b border-gray-200">
        <div className="flex justify-between items-end">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {['users', 'charities', 'pledges'].map((tabName) => (
              <button
                key={tabName}
                onClick={() => setActiveTab(tabName)}
                className={`${
                  activeTab === tabName
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
              >
                {tabName}
              </button>
            ))}
          </nav>
          
          {/* Action Buttons */}
          <div className="pb-4 flex space-x-3">
            {/* Add Button - only show for charities and pledges */}
            {(activeTab === 'charities' || activeTab === 'pledges') && (
              <button
                onClick={handleAddClick}
                disabled={loading || refreshing}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  loading || refreshing
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                } transition-colors duration-200`}
                title={`Add new ${activeTab.slice(0, -1)}`}
              >
                <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add {activeTab === 'charities' ? 'Charity' : 'Pledge'}
              </button>
            )}
            
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                loading || refreshing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              } transition-colors duration-200`}
              title={`Refresh ${activeTab} data`}
            >
              {refreshing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Refreshing...
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {activeTab === 'users' && renderTable(users, 'users', userColumns)}
      {activeTab === 'charities' && renderTable(charities, 'charities', charityColumns)}
      {activeTab === 'pledges' && renderTable(pledges, 'pledges', pledgeColumns)}

      {/* Add Item Modal */}
      <AddItemModal
        isOpen={showAddModal}
        onClose={closeAddModal}
        title={addFormType === 'charity' ? 'Add New Charity' : 'Add New Pledge'}
      >
        {addFormType === 'charity' && (
          <CharityForm
            onSuccess={handleAddSuccess}
            onError={handleAddError}
          />
        )}
        {addFormType === 'pledge' && (
          <PledgeForm
            onSuccess={handleAddSuccess}
            onError={handleAddError}
          />
        )}
      </AddItemModal>

      {showConfirmModal && itemToDelete && (
        <ConfirmModal
          isOpen={showConfirmModal}
          title={`Confirm Deletion`}
          message={deleteMessage}
          onConfirm={confirmDelete}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </div>
  );
};

export default HomeView;
